/**
 * OrgNode - Custom node component for ReactFlow
 * Displays user information in the organizational chart
 */

import React from 'react';
import { Handle, Position } from 'reactflow';

const avatarCache = {};

const getInitialsAvatar = (fullName) => {
  if (avatarCache[fullName]) return avatarCache[fullName];
  if (!fullName) return null;
  const initials = fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
      <rect width="100%" height="100%" fill="#d4af37" />
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
        font-family="Arial, sans-serif" font-size="80" fill="#ffffff">${initials}</text>
    </svg>`;

  const base64 = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  avatarCache[fullName] = base64;
  return base64;
};

/**
 * Custom node component for org chart
 */
const OrgNode = React.memo(function OrgNode({ data, selected }) {
  const {
    name = 'Unknown',
    plan = '',
    agencyCode= '',
    email = '',
    profilePicture = '',
    photoURL = '',
    level = '',
    isBrokerSubRoot = false,
    isFocused = false,
    highlightColor = null,
    isDimmed = false,
    isCollapsed = false,
    hasChildren = false,
    onToggleCollapse = null,
    childrenCount = 0
  } = data;

  const isBroker = plan === 'Broker';
  const profileImg = profilePicture || photoURL || getInitialsAvatar(name);

  const classes = [
    'org-node',
    isBroker ? 'org-node-broker' : 'org-node-regular',
    isBrokerSubRoot ? 'org-node-subroot' : '',
    selected ? 'org-node-selected' : '',
    isFocused ? 'org-node-focused' : '',
    highlightColor ? 'highlight' : '',
    isDimmed ? 'dimmed' : '',
    isCollapsed ? 'collapsed' : ''
  ].filter(Boolean).join(' ');

  const baseStyle = {
    width: '180px',
    height: '220px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    background: '#ffffff',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  // Allow dynamic background/border styling via filter selection
  const nodeStyle = {
    ...baseStyle,
    ...(data?.filterBackground || {})
  };
  
  const handleExpandClick = (e) => {
    e.stopPropagation();
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  return (
    <div className={classes} style={nodeStyle}>
      {/* Handles for connections */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#94a3b8', width: 8, height: 8 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#94a3b8', width: 8, height: 8 }}
      />

      {/* Collapsed count badge */}
      {isCollapsed && childrenCount > 0 && (
        <div className="collapsed-count-badge">
          {childrenCount}
        </div>
      )}

      {/* Profile image */}
      <div className="org-node-avatar">
        <img
          src={profileImg}
          className="org-node-avatar-img"
          alt={name}
          onError={(e) => {
            // Fallback to initials avatar if the image fails
            e.target.onerror = null;
            e.target.src = getInitialsAvatar(name);
          }}
        />
      </div>

      {/* User name */}
      <div className="org-chart-node-name">{name}</div>

      <div className="org-node-agencycode">{agencyCode}</div>
      {/* Account type */}
      <div className="org-node-level">{level}</div>

      {/* Email */}

      {/* Expand/Collapse button */}
      {hasChildren && (
        <button
          className="org-node-expand-btn"
          onClick={handleExpandClick}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? '+' : '−'}
        </button>
      )}
    </div>
  );
});

/**
 * Broker card component for SuperBase grid view
 */
export const BrokerCard = React.memo(function BrokerCard({ data }) {
  const {
    name = 'Unknown',
    plan = '',
    level = '',
    agencyCode = '',
    email = '',
    broker,
    isSelected = false,
    onClick,
  } = data;

  return (
    <div
      className={`broker-card ${isSelected ? 'broker-card-selected' : ''}`}
      onClick={() => {
        if (onClick) onClick(broker);
      }}
    >
      <div className="broker-card-header">
        <div className="broker-card-title">{name}</div>
      {agencyCode && <div className="broker-card-email">{agencyCode}</div>}
      </div>
        <div className="broker-card-badge">{level || 'Broker'}</div>


      <div className="broker-card-action">View BaseShop →</div>
    </div>
  );
});

export default OrgNode;